import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { Course } from "@features/Referback/steps/Courses/types";

export interface OrganizationCategory {
	id: number;
	uuid: string;
	name: string;
}

export interface ReferbackState {
	selectedCourse: Course | null;
	selectedApplicationCourseId: string | null;
	completedCourses: string[];
	organizationCategory: OrganizationCategory | null;
	lastStepByApplicationUuid: Record<string, string>;
}

const initialState: ReferbackState = {
	selectedCourse: null,
	selectedApplicationCourseId: null,
	completedCourses: [],
	organizationCategory: null,
	lastStepByApplicationUuid: {},
};

const referbackSlice = createSlice({
	name: "referback",
	initialState,
	reducers: {
		setSelectedCourse(state, action: PayloadAction<Course | null>) {
			state.selectedCourse = action.payload;
			state.selectedApplicationCourseId = action.payload?.uuid || null;
		},
		setSelectedApplicationCourseId(state, action: PayloadAction<string | null>) {
			state.selectedApplicationCourseId = action.payload;
		},
		addCompletedCourse(state, action: PayloadAction<string>) {
			if (!state.completedCourses.includes(action.payload)) {
				state.completedCourses.push(action.payload);
			}
		},
		removeCompletedCourse(state, action: PayloadAction<string>) {
			state.completedCourses = state.completedCourses.filter(id => id !== action.payload);
		},
		clearCompletedCourses(state) {
			state.completedCourses = [];
		},
		setOrganizationCategory(state, action: PayloadAction<OrganizationCategory | null>) {
			state.organizationCategory = action.payload;
		},
		setReferbackLastStep(state, action: PayloadAction<{ applicationUuid: string; step: string }>) {
			const { applicationUuid, step } = action.payload;
			state.lastStepByApplicationUuid[applicationUuid] = step;
		},
		clearReferbackLastStep(state, action: PayloadAction<string>) {
			delete state.lastStepByApplicationUuid[action.payload];
		},
		resetReferback(state) {
			state.selectedCourse = null;
			state.selectedApplicationCourseId = null;
			state.completedCourses = [];
			state.organizationCategory = null;
			state.lastStepByApplicationUuid = {};
		},
	},
});

export const {
	setSelectedCourse,
	setSelectedApplicationCourseId,
	addCompletedCourse,
	removeCompletedCourse,
	clearCompletedCourses,
	setOrganizationCategory,
	setReferbackLastStep,
	clearReferbackLastStep,
	resetReferback,
} = referbackSlice.actions;

export default referbackSlice.reducer;

